import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { worldCountries } from '@/data/countries/worldCountriesExport';
import type { WorldCountry } from '@/data/countries/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Play, Pause, RefreshCw, CheckCircle, AlertCircle, Globe, Database, Clock, DollarSign } from 'lucide-react';
import { CityLanguage } from '@/context/languages/languageData';
import { countryLanguageMap } from '@/utils/cityLanguage/countryLanguageMap';

interface LanguageStats {
  language: CityLanguage;
  languageName: string;
  totalCities: number;
  translatedCities: number;
  pendingCities: number;
  progress: number;
}

interface TranslationStats {
  totalCities: number;
  citiesNeedingTranslation: number;
  byLanguage: LanguageStats[];
  estimatedHours: number;
  estimatedCost: number;
}

interface JobProgress {
  jobId: string;
  status: 'pending' | 'processing' | 'paused' | 'completed' | 'error';
  totalRecords: number;
  processedRecords: number;
  successCount: number;
  errorCount: number;
  progress: number;
  startedAt?: Date;
  eta?: string;
  speed?: number; // ciudades por minuto
}

const cityLanguageNames: Partial<Record<CityLanguage, string>> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  it: 'Italiano',
  pt: 'Português',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
  ar: 'العربية',
  hi: 'हिन्दी',
  ru: 'Русский',
  tr: 'Türkçe',
  th: 'ไทย',
  vi: 'Tiếng Việt',
  id: 'Bahasa Indonesia',
  nl: 'Nederlands',
  ur: 'اردو',
  bn: 'বাংলা',
  fa: 'فارسی',
  ms: 'Bahasa Melayu',
  da: 'Dansk',
  no: 'Norsk',
  fi: 'Suomi',
  sv: 'Svenska',
  is: 'Íslenska',
  cs: 'Čeština',
  sk: 'Slovenčina',
  sl: 'Slovenščina',
  pl: 'Polski',
  et: 'Eesti',
  lv: 'Latviešu',
  lt: 'Lietuvių',
  el: 'Ελληνικά',
  ro: 'Română',
  bg: 'Български',
  hr: 'Hrvatski',
  hu: 'Magyar',
  uk: 'Українська',
  he: 'עברית',
  tl: 'Tagalog',
  sw: 'Kiswahili'
};

const getLanguageFlag = (language: CityLanguage): string => {
  const flags: Partial<Record<CityLanguage, string>> = {
    en: '🇬🇧', es: '🇪🇸', fr: '🇫🇷', de: '🇩🇪', it: '🇮🇹', pt: '🇵🇹',
    zh: '🇨🇳', ja: '🇯🇵', ko: '🇰🇷', ar: '🇦🇪', hi: '🇮🇳', ru: '🇷🇺',
    tr: '🇹🇷', th: '🇹🇭', vi: '🇻🇳', id: '🇮🇩', nl: '🇳🇱', ur: '🇵🇰',
    bn: '🇧🇩', fa: '🇮🇷', ms: '🇲🇾',
    da: '🇩🇰', no: '🇳🇴', fi: '🇫🇮', sv: '🇸🇪', is: '🇮🇸',
    cs: '🇨🇿', sk: '🇸🇰', sl: '🇸🇮', pl: '🇵🇱',
    et: '🇪🇪', lv: '🇱🇻', lt: '🇱🇹',
    el: '🇬🇷', ro: '🇷🇴', bg: '🇧🇬', hr: '🇭🇷', hu: '🇭🇺',
    uk: '🇺🇦', he: '🇮🇱', tl: '🇵🇭', sw: '🇰🇪'
  };
  return flags[language] || '🌐';
};

export const MassiveTranslationControl = () => {
  const { toast } = useToast();
  const [stats, setStats] = useState<TranslationStats | null>(null);
  const [jobProgress, setJobProgress] = useState<JobProgress | null>(null);
  const [isCalculating, setIsCalculating] = useState(true);
  const [isStarting, setIsStarting] = useState(false);

  // Calculate initial statistics
  useEffect(() => {
    calculateStats();
  }, []);

  // Poll job status every 5 seconds for progress updates
  useEffect(() => {
    if (!jobProgress?.jobId) return;

    const interval = setInterval(() => {
      fetchJobStatus(jobProgress.jobId);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [jobProgress?.jobId]);

  const calculateStats = async () => {
    setIsCalculating(true);
    
    try {
      // Get all cities from worldCountries and group by language
      const allCities = worldCountries.flatMap(country =>
        country.topCities.map(city => {
          const countryCode = country.code.toUpperCase();
          const language = countryLanguageMap[countryCode] || 'en';
          
          return {
            slug: city.slug,
            name: city.name,
            countryName: country.name,
            countryCode: countryCode,
            language: language as CityLanguage
          };
        })
      );

      console.log(`📊 Total cities loaded: ${allCities.length}`);

      // Count cities by language
      const languageGroups = allCities.reduce((acc, city) => {
        const lang = city.language;
        if (!acc[lang]) {
          acc[lang] = [];
        }
        acc[lang].push(city);
        return acc;
      }, {} as Record<CityLanguage, typeof allCities>);

      const { data: existingTranslations } = await supabase
        .from('city_translations')
        .select('city_slug, language');

      const translationMap = new Map<string, Set<string>>();
      if (existingTranslations) {
        existingTranslations.forEach(t => {
          if (!translationMap.has(t.city_slug)) {
            translationMap.set(t.city_slug, new Set());
          }
          translationMap.get(t.city_slug)!.add(t.language);
        });
      }

      // Calculate stats per language
      const byLanguage: LanguageStats[] = Object.entries(languageGroups)
        .filter(([lang]) => lang !== 'en') // Exclude English
        .map(([lang, cities]) => {
          const language = lang as CityLanguage;
          const totalCities = cities.length;
          const translatedCities = cities.filter(city =>
            translationMap.get(city.slug)?.has(language)
          ).length;
          const pendingCities = totalCities - translatedCities;

          return {
            language,
            languageName: cityLanguageNames[language] || language,
            totalCities,
            translatedCities,
            pendingCities,
            progress: totalCities > 0 ? Math.round((translatedCities / totalCities) * 100) : 0
          };
        })
        .sort((a, b) => b.totalCities - a.totalCities);

      const citiesNeedingTranslation = byLanguage.reduce(
        (sum, lang) => sum + lang.pendingCities,
        0
      );

      // Estimations (10 cities per minute based on batch processing)
      const estimatedMinutes = citiesNeedingTranslation / 10;
      const estimatedHours = estimatedMinutes / 60;
      const estimatedCost = citiesNeedingTranslation * 0.002; // $0.002 per city

      setStats({
        totalCities: allCities.length,
        citiesNeedingTranslation,
        byLanguage,
        estimatedHours,
        estimatedCost
      });

      // Check for active jobs
      const { data: activeJobs } = await supabase
        .from('translation_jobs')
        .select('*')
        .in('status', ['pending', 'processing', 'paused'])
        .order('created_at', { ascending: false })
        .limit(1);

      if (activeJobs && activeJobs.length > 0) {
        const job = activeJobs[0];
        const status = job.status as 'pending' | 'processing' | 'paused' | 'completed' | 'error';
        setJobProgress({
          jobId: job.id,
          status,
          totalRecords: job.total_records,
          processedRecords: job.processed_records,
          successCount: job.success_count,
          errorCount: job.error_count,
          progress: job.total_records > 0 
            ? Math.round((job.processed_records / job.total_records) * 100)
            : 0
        });
      }
    } catch (error) {
      console.error('Error calculating stats:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron calcular las estadísticas',
        variant: 'destructive'
      });
    } finally {
      setIsCalculating(false);
    }
  };

  const updateJobProgress = (payload: any) => {
    if (!jobProgress) return;

    setJobProgress(prev => {
      if (!prev) return null;
      
      const processed = payload.processed_records || prev.processedRecords;
      const total = prev.totalRecords;
      const progress = total > 0 ? Math.round((processed / total) * 100) : 0;

      // Calculate speed and ETA
      const now = new Date();
      const startedAt = prev.startedAt || now;
      const elapsedMinutes = (now.getTime() - startedAt.getTime()) / 1000 / 60;
      const speed = elapsedMinutes > 0 ? processed / elapsedMinutes : 0;
      const remainingCities = total - processed;
      const remainingMinutes = speed > 0 ? remainingCities / speed : 0;
      const eta = remainingMinutes > 60 
        ? `${Math.round(remainingMinutes / 60)}h ${Math.round(remainingMinutes % 60)}m`
        : `${Math.round(remainingMinutes)}m`;

      return {
        ...prev,
        processedRecords: processed,
        successCount: payload.success_count || prev.successCount,
        errorCount: payload.error_count || prev.errorCount,
        progress,
        speed: Math.round(speed * 10) / 10,
        eta
      };
    });
  };

  const fetchJobStatus = async (jobId: string) => {
    const { data } = await supabase
      .from('translation_jobs')
      .select('*')
      .eq('id', jobId)
      .single();

    if (data) {
      updateJobProgress(data);
      
      if (data.status === 'completed') {
        toast({
          title: '¡Traducción completada!',
          description: `${data.success_count} ciudades traducidas exitosamente`,
        });
        calculateStats(); // Refresh stats
      }
    }
  };

  const startFullTranslation = async () => {
    if (!stats || stats.citiesNeedingTranslation === 0) {
      toast({
        title: 'No hay traducciones pendientes',
        description: 'Todas las ciudades ya están traducidas',
      });
      return;
    }

    setIsStarting(true);

    try {
      // 1. Create translation job
      const { data: job, error: jobError } = await supabase.functions.invoke(
        'start-translation-job',
        {
          body: {
            totalRecords: stats.citiesNeedingTranslation,
            params: { mode: 'full', languages: stats.byLanguage.map(l => l.language) }
          }
        }
      );

      if (jobError) throw jobError;

      toast({
        title: 'Job de traducción iniciado',
        description: `ID: ${job.jobId}`,
      });

      setJobProgress({
        jobId: job.jobId,
        status: 'pending',
        totalRecords: stats.citiesNeedingTranslation,
        processedRecords: 0,
        successCount: 0,
        errorCount: 0,
        progress: 0,
        startedAt: new Date()
      });

      // 2. Enqueue translation records
      const allCities = worldCountries.flatMap(country =>
        country.topCities.map(city => ({
          city_slug: city.slug,
          local_language: (countryLanguageMap[country.code.toUpperCase()] || 'en') as CityLanguage
        }))
      ).filter(city => city.local_language !== 'en');

      const { error: enqueueError } = await supabase.functions.invoke(
        'enqueue-translation-records',
        {
          body: {
            jobId: job.jobId,
            records: allCities
          }
        }
      );

      if (enqueueError) throw enqueueError;

      // Force immediate status fetch to update UI
      await fetchJobStatus(job.jobId);

      toast({
        title: 'Traducción en progreso',
        description: 'El proceso se ejecuta automáticamente en segundo plano',
      });

      // Safety check: verify job doesn't complete prematurely with 0 records
      setTimeout(async () => {
        const { data: jobCheck } = await supabase
          .from('translation_jobs')
          .select('*')
          .eq('id', job.jobId)
          .single();
        
        if (jobCheck && jobCheck.status === 'completed' && jobCheck.processed_records === 0) {
          console.error('🚨 RACE CONDITION DETECTED: Job completed with 0 records processed');
          toast({
            title: 'Error detectado',
            description: 'El job se completó prematuramente. Usa "Detener y limpiar" e inicia de nuevo.',
            variant: 'destructive'
          });
        }
      }, 10000); // Check after 10 seconds

    } catch (error: any) {
      console.error('Error starting translation:', error);
      toast({
        title: 'Error al iniciar traducción',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsStarting(false);
    }
  };

  const pauseTranslation = async () => {
    if (!jobProgress?.jobId) return;

    try {
      await supabase.functions.invoke('pause-translation-job', {
        body: { jobId: jobProgress.jobId }
      });

      setJobProgress(prev => prev ? { ...prev, status: 'paused' } : null);

      toast({
        title: 'Traducción pausada',
        description: 'Puedes reanudarla cuando quieras',
      });
    } catch (error: any) {
      toast({
        title: 'Error al pausar',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const resumeTranslation = async () => {
    if (!jobProgress?.jobId) return;

    try {
      await supabase.functions.invoke('resume-translation-job', {
        body: { jobId: jobProgress.jobId }
      });

      setJobProgress(prev => prev ? { ...prev, status: 'processing' } : null);

      toast({
        title: 'Traducción reanudada',
        description: 'El proceso continúa en segundo plano',
      });
    } catch (error: any) {
      toast({
        title: 'Error al reanudar',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const cancelTranslation = async () => {
    if (!jobProgress?.jobId) return;
    try {
      const { error } = await supabase.functions.invoke('cancel-translation-job', {
        body: { jobId: jobProgress.jobId }
      });
      if (error) throw error;

      setJobProgress(null);
      toast({
        title: 'Traducción cancelada',
        description: 'Cola limpiada. Puedes iniciar de nuevo.',
      });
      calculateStats();
    } catch (error: any) {
      toast({
        title: 'Error al cancelar',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  if (isCalculating) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 animate-spin mx-auto mb-4 text-primary" />
            <p className="text-lg">Calculando estadísticas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2">Control de Traducción Masiva</h1>
          <p className="text-muted-foreground">
            Sistema automático de traducción para todas las city landings
          </p>
        </div>
        <Globe className="w-16 h-16 text-primary opacity-20" />
      </div>

      {/* Overall Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Ciudades</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalCities.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">En {worldCountries.length} países</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.citiesNeedingTranslation.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Requieren traducción</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tiempo Estimado</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.estimatedHours ? Math.round(stats.estimatedHours) : 0}h
            </div>
            <p className="text-xs text-muted-foreground">~10 ciudades/minuto</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Costo Estimado</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${stats?.estimatedCost ? Math.round(stats.estimatedCost) : 0}
            </div>
            <p className="text-xs text-muted-foreground">Lovable AI Gateway</p>
          </CardContent>
        </Card>
      </div>

      {/* Job Progress */}
      {jobProgress && (
        <Card className="border-primary">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Progreso de Traducción</CardTitle>
                <CardDescription>
                  Job ID: {jobProgress.jobId}
                </CardDescription>
              </div>
              <Badge variant={
                jobProgress.status === 'completed' ? 'default' :
                jobProgress.status === 'processing' ? 'secondary' :
                jobProgress.status === 'paused' ? 'outline' :
                'destructive'
              }>
                {jobProgress.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Progreso: {jobProgress.processedRecords} / {jobProgress.totalRecords}</span>
                <span className="font-bold">{jobProgress.progress}%</span>
              </div>
              <Progress value={jobProgress.progress} className="h-3" />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Exitosas</p>
                <p className="text-lg font-bold text-green-600">{jobProgress.successCount}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Errores</p>
                <p className="text-lg font-bold text-red-600">{jobProgress.errorCount}</p>
              </div>
              {jobProgress.speed && (
                <div>
                  <p className="text-muted-foreground">Velocidad</p>
                  <p className="text-lg font-bold">{jobProgress.speed} c/min</p>
                </div>
              )}
              {jobProgress.eta && (
                <div>
                  <p className="text-muted-foreground">ETA</p>
                  <p className="text-lg font-bold">{jobProgress.eta}</p>
                </div>
              )}
            </div>

            <div className="flex gap-2">
              {jobProgress.status === 'processing' && (
                <Button onClick={pauseTranslation} variant="outline">
                  <Pause className="w-4 h-4 mr-2" />
                  Pausar
                </Button>
              )}
              {jobProgress.status === 'paused' && (
                <Button onClick={resumeTranslation}>
                  <Play className="w-4 h-4 mr-2" />
                  Reanudar
                </Button>
              )}
              <Button onClick={cancelTranslation} variant="destructive">
                <AlertCircle className="w-4 h-4 mr-2" />
                Detener y limpiar
              </Button>
              <Button onClick={() => calculateStats()} variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Language Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Desglose por Idioma</CardTitle>
          <CardDescription>
            Estado de traducción para cada idioma local
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats?.byLanguage.map((lang) => (
              <div key={lang.language} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{getLanguageFlag(lang.language)}</span>
                    <div>
                      <p className="font-medium">{lang.languageName}</p>
                      <p className="text-xs text-muted-foreground">
                        {lang.translatedCities} / {lang.totalCities} ciudades
                      </p>
                    </div>
                  </div>
                  <Badge variant={lang.progress === 100 ? 'default' : 'secondary'}>
                    {lang.progress}%
                  </Badge>
                </div>
                <Progress value={lang.progress} className="h-2" />
                {lang.pendingCities > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {lang.pendingCities} ciudades pendientes
                  </p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {!jobProgress && stats && stats.citiesNeedingTranslation > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Iniciar Traducción</CardTitle>
            <CardDescription>
              Este proceso se ejecutará automáticamente en segundo plano
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted p-4 rounded-lg space-y-2 text-sm">
              <p className="font-medium">📋 Proceso a ejecutar:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Se traducirán {stats.citiesNeedingTranslation.toLocaleString()} ciudades</li>
                <li>Tiempo estimado: ~{Math.round(stats.estimatedHours)} horas</li>
                <li>Costo estimado: ~${Math.round(stats.estimatedCost)} USD</li>
                <li>El proceso continúa aunque cierres el navegador</li>
                <li>Puedes pausar/reanudar cuando quieras</li>
              </ul>
            </div>

            <Button 
              onClick={startFullTranslation} 
              disabled={isStarting}
              size="lg"
              className="w-full"
            >
              {isStarting ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Iniciando...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Iniciar Traducción Completa
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {stats && stats.citiesNeedingTranslation === 0 && !jobProgress && (
        <Card className="border-green-500">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
              <div>
                <h3 className="text-2xl font-bold">¡Traducción Completa!</h3>
                <p className="text-muted-foreground mt-2">
                  Todas las ciudades han sido traducidas a sus idiomas locales
                </p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <p className="text-sm">
                  ✅ {stats.totalCities.toLocaleString()} ciudades en {stats.byLanguage.length} idiomas
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};